import { v4 as uuidv4 } from 'uuid'
import {
  demoCategories,
  demoCustomers,
  demoProducts,
  demoRegister,
} from "@/data/demo";
import { storage } from "@/lib/storage";
import { calculateTotals } from "@/services/PricingService";
import { PosHttpParams, PosModel } from "@/types";

import { HttpRequest, HttpResponse } from "./types";

const DB_KEY = "iws-pos:demo-db";

/** Mutable copy of the demo data, saved to the browser so sales, stock and new customers survive a reload. */
const db = storage.read(DB_KEY, {
  products: structuredClone(demoProducts),
  customers: structuredClone(demoCustomers),
  orders: [] as PosModel.Order[],
});
const save = () => storage.write(DB_KEY, db);

const latency = () =>
  new Promise((r) => setTimeout(r, 120 + Math.random() * 180));
const ok = <T>(data: unknown): HttpResponse<T> => ({
  success: true,
  data: data as T,
});
const fail = <T>(status: number, message: string): HttpResponse<T> => ({
  success: false,
  error: { status, message },
});
const match = (q: string | undefined, ...fields: (string | undefined)[]) =>
  !q || fields.some((f) => f?.toLowerCase().includes(q.toLowerCase()));

let orderSeq = 1040 + db.orders.length;

function createOrder(
  body: PosHttpParams.CreateOrder,
): HttpResponse<PosModel.Order> {
  const lines: PosModel.CartLine[] = [];
  for (const l of body.lines) {
    const product = db.products.find((p) => p._id === l.product_id);
    if (!product) return fail(422, `Product ${l.product_id} not found`);
    if (product.stock < l.quantity)
      return fail(409, `Only ${product.stock} ${product.name} left in stock`);
    lines.push({
      product: { ...product },
      quantity: l.quantity,
      discount: l.discount,
      note: l.note,
    });
  }
  const totals = calculateTotals(lines, body.discount, demoRegister.tax_rate, body.adjustment ?? 0);
  const paid = body.payments.reduce((s, p) => s + p.amount, 0);
  if (paid + 0.001 < totals.total)
    return fail(422, "Payment does not cover the total");

  for (const l of lines) {
    const product = db.products.find((p) => p._id === l.product._id);
    if (product) product.stock -= l.quantity;
  }
  const order: PosModel.Order = {
    _id: uuidv4(),
    number: `POS-${++orderSeq}`,
    created_at: new Date().toISOString(),
    cashier: demoRegister.cashier,
    customer: db.customers.find((c) => c._id === body.customer_id),
    lines,
    discount: body.discount,
    totals,
    payments: body.payments,
    change: Math.round((paid - totals.total) * 100) / 100,
    status: "completed",
  };
  db.orders.unshift(order);
  save();
  return ok(order);
}

type Handler = (ctx: {
  params: string[];
  q: Record<string, string | undefined>;
  body: unknown;
}) => HttpResponse<unknown>;

const routes: [string, RegExp, Handler][] = [
  ["get", /^\/pos\/register$/, () => ok(demoRegister)],
  ["get", /^\/pos\/categories$/, () => ok(demoCategories)],
  [
    "get",
    /^\/pos\/products$/,
    ({ q }) => {
      const items = db.products.filter(
        (p) =>
          (!q.category_id || p.category_id === q.category_id) &&
          (!q.low_stock || p.stock <= p.low_stock_threshold) &&
          match(q.search, p.name, p.sku, ...p.barcodes),
      );
      return ok({ items, total: items.length });
    },
  ],
  [
    "get",
    /^\/pos\/products\/barcode\/([^/]+)$/,
    ({ params: [code] }) => {
      const product = db.products.find(
        (p) => p.barcodes.includes(code),
      );
      return product
        ? ok(product)
        : fail(404, `No product with barcode ${code}`);
    },
  ],
  [
    "get",
    /^\/pos\/customers$/,
    ({ q }) => {
      const items = db.customers.filter((c) =>
        match(q.search, c.name, c.phone, c.email),
      );
      return ok({ items, total: items.length });
    },
  ],
  [
    "post",
    /^\/pos\/customers$/,
    ({ body }) => {
      const input = body as PosHttpParams.CreateCustomer;
      if (db.customers.some((c) => c.phone === input.phone))
        return fail(409, "A customer with this phone already exists");
      const customer: PosModel.Customer = {
        _id: `c-${uuidv4().slice(0, 8)}`,
        loyalty_points: 0,
        total_orders: 0,
        ...input,
      };
      db.customers.unshift(customer);
      save();
      return ok(customer);
    },
  ],
  [
    "get",
    /^\/pos\/orders$/,
    ({ q }) => {
      const items = db.orders.filter((o) =>
        match(q.search, o.number, o.customer?.name, o.customer?.phone),
      );
      return ok({ items, total: items.length });
    },
  ],
  [
    "post",
    /^\/pos\/orders$/,
    ({ body }) => createOrder(body as PosHttpParams.CreateOrder),
  ],
  [
    "post",
    /^\/pos\/orders\/([^/]+)\/refund$/,
    ({ params: [id] }) => {
      const order = db.orders.find((o) => o._id === id);
      if (!order) return fail(404, "Order not found");
      if (order.status === "refunded")
        return fail(409, "Order already refunded");
      for (const l of order.lines) {
        const product = db.products.find((p) => p._id === l.product._id);
        if (product) product.stock += l.quantity;
      }
      order.status = "refunded";
      save();
      return ok(order);
    },
  ],
];

/** Routes requests the way the backoffice API would, against the demo dataset. */
export async function handleMockRequest<T>({
  method,
  url,
  query = {},
  body,
}: HttpRequest): Promise<HttpResponse<T>> {
  await latency();
  for (const [m, pattern, handler] of routes) {
    const found = m === method && url.match(pattern);
    if (found)
      return handler({
        params: found.slice(1),
        q: query as Record<string, string | undefined>,
        body,
      }) as HttpResponse<T>;
  }
  return fail(404, `No demo route for ${method.toUpperCase()} ${url}`);
}
